/**
 * Channel-modifier schema v8, as the wizard authors it.
 *
 * v8 turns a Before modifier's expected sequence into a PROGRAM: besides
 * waiting for a change (WAIT, every older step), a step can release the held
 * command (RELEASE), send a Control message (SEND) or wait for the controller's
 * acknowledgement (AWAIT_ACK). A step names its own channel and floor, can pass
 * at once when its level is already current, and can be marked always-run so
 * cleanup — re-securing a floor — happens even after an earlier step failed.
 * PRECONDITIONS on current channel state decide whether the modifier acts at
 * all, and a post-release budget bounds the steps after the release.
 *
 * Every v8 field defaults to the pre-v8 meaning, and VTS skips a row whose
 * schema_version it does not know, so a modifier becomes v8 only when a v8 field
 * is actually in use — and a modifier that uses none is sent exactly as the v7
 * rules send it. As for v7, a field is sent only while the control that sets it
 * is shown.
 *
 * Everything here is pure, so the rules the form enforces are testable without
 * rendering the form.
 */
import { ActiveState } from "../../../flatbuffers/generated/archive/active-state";
import { ModifierDirection } from "../../../flatbuffers/generated/archive/modifier-direction";
import { ModifierDropAction } from "../../../flatbuffers/generated/archive/modifier-drop-action";
import { ModifierSequenceStepT } from "../../../flatbuffers/generated/archive/modifier-sequence-step";
import { ModifierStateConditionT } from "../../../flatbuffers/generated/archive/modifier-state-condition";
import { ModifierTimeoutPolicy } from "../../../flatbuffers/generated/archive/modifier-timeout-policy";
import { ModifierType } from "../../../flatbuffers/generated/archive/modifier-type";
import { PreconditionUnknown } from "../../../flatbuffers/generated/archive/precondition-unknown";
import { SequenceStepKind } from "../../../flatbuffers/generated/archive/sequence-step-kind";
import { SequenceStepScope } from "../../../flatbuffers/generated/archive/sequence-step-scope";
import { StepPositionSource } from "../../../flatbuffers/generated/archive/step-position-source";
import type { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { stringToUuidT, uuidToString, ZERO_UUID_STRING } from "../../../utils/uuid";
import {
  effectiveV7Fields,
  enumMember,
  isSequenceVisible,
  requiredSchemaVersion,
  type EffectiveV7Fields,
  type FormStep,
  type LeafRef,
  type ObservableChannel,
  type V7Form,
  type V7ModifierFields,
} from "./channelModifierV7";

/** The schema version of a modifier that uses any v8 field. */
export const MODIFIER_SCHEMA_V8 = 8;

/** The schema version of a modifier whose payload active mirrors or inverts the source's. */
export const MODIFIER_SCHEMA_V9 = 9;

/** What VTS uses for a post-release budget of 0: ten minutes. */
export const DEFAULT_POST_RELEASE_BUDGET_MS = 600_000;

/** The per-step fields v8 added, as the form holds them. */
export interface ProgramStepFields<R extends LeafRef = LeafRef> {
  kind: SequenceStepKind;
  /** WAIT: the channel to observe; "" means the modifier's observe channel. */
  observeChannelId: string;
  positionSource: StepPositionSource;
  satisfiedIfCurrent: boolean;
  alwaysRun: boolean;
  /** SEND: the channel to send to; "" means the modifier's target. */
  targetChannelId: string;
  /** SEND: the Control leaf to send. */
  sendRef: R | null;
  /** SEND: the active flag stamped on the sent message. */
  sendActive: ActiveState;
  label: string;
}

/** The v8 fields of a new step: a WAIT that uses nothing v8 added. */
export const DEFAULT_PROGRAM_STEP_FIELDS: Readonly<ProgramStepFields<never>> = {
  kind: SequenceStepKind.WAIT,
  observeChannelId: "",
  positionSource: StepPositionSource.PAYLOAD,
  satisfiedIfCurrent: false,
  alwaysRun: false,
  targetChannelId: "",
  sendRef: null,
  sendActive: ActiveState.ACTIVE,
  label: "",
};

/** One step as the form holds it: the v5/v7 fields and the v8 ones. */
export type ProgramFormStep = FormStep & ProgramStepFields;

/** One precondition as the form holds it. */
export interface PreconditionRow<R extends LeafRef = LeafRef> {
  channelId: string;
  messageRef: R | null;
  /** ACTIVE or INACTIVE; VTS and LiftCloud refuse UNSET. */
  expectedActive: ActiveState;
  positionSource: StepPositionSource;
  scope: SequenceStepScope;
  ifUnknown: PreconditionUnknown;
}

/**
 * A new precondition: "is active now", at the payload's floor. Unknown state
 * reads as NOT met, so a modifier never acts on a level its channel has not
 * reported yet.
 */
export const NEW_PRECONDITION: Readonly<PreconditionRow<never>> = {
  channelId: "",
  messageRef: null,
  expectedActive: ActiveState.ACTIVE,
  positionSource: StepPositionSource.PAYLOAD,
  scope: SequenceStepScope.SAME_POSITION,
  ifUnknown: PreconditionUnknown.ASSUME_NOT_MET,
};

/** The form fields the v8 rules read. */
export interface V8Form extends V7Form {
  sourceChannelId: string;
  expectedSequence: readonly ProgramFormStep[];
  preconditions: readonly PreconditionRow[];
  postReleaseBudgetMs: number;
}

/** What the v8 version rule reads: the v7 fields, and the v8 ones as sent. */
export interface V8ModifierFields extends V7ModifierFields {
  programSteps: readonly ModifierSequenceStepT[];
  preconditions: readonly ModifierStateConditionT[];
  postReleaseBudgetMs: number;
}

/** Every field a save sends that v7 or v8 added, and the version they need. */
export interface EffectiveV8Fields extends Omit<EffectiveV7Fields, "steps"> {
  /** The v7 rules' steps, every v8 field at its default, unless `schemaVersion` is 8. */
  steps: ModifierSequenceStepT[];
  preconditions: ModifierStateConditionT[];
  postReleaseBudgetMs: number;
}

/**
 * Whether a step uses anything schema v8 added.
 *
 * Mirrors VTS's `step_uses_v8` field for field, so the wizard and VTS agree on
 * which rows an older VTS will skip.
 */
export function stepUsesV8(step: ModifierSequenceStepT): boolean {
  return (
    step.kind !== SequenceStepKind.WAIT ||
    step.observeChannelId !== null ||
    step.positionSource !== StepPositionSource.PAYLOAD ||
    step.satisfiedIfCurrent ||
    step.alwaysRun ||
    step.targetChannelId !== null ||
    step.sendMessageContent !== MessageContent.NONE ||
    step.sendMessageValue !== 0 ||
    step.payloadActive !== ActiveState.UNSET ||
    step.label !== null
  );
}

/**
 * Whether a payload active is relative to the source message's own active
 * (MIRROR, INVERT), which only schema v9 knows.
 */
export function isRelativePayloadActive(active: ActiveState): boolean {
  return active === ActiveState.MIRROR || active === ActiveState.INVERT;
}

/** What each payload-active choice does, in plain words, for the line under the select. */
const PAYLOAD_ACTIVE_HELP: Readonly<Partial<Record<ActiveState, string>>> = {
  [ActiveState.UNSET]: "The target message is always sent with active true.",
  [ActiveState.INACTIVE]: "The target message is always sent with active false.",
  [ActiveState.MIRROR]:
    "The target message is sent with the same active as the source message. Needs schema v9.",
  [ActiveState.INVERT]:
    "The target message is sent with the opposite active of the source message. Needs schema v9.",
};

/**
 * The option the payload-active select shows: ACTIVE reads as the default, as
 * UNSET does, and anything the select does not offer reads as the default too.
 */
export function payloadActiveDisplay(active: ActiveState): ActiveState {
  return active in PAYLOAD_ACTIVE_HELP ? active : ActiveState.UNSET;
}

/** One plain sentence on what the chosen payload active does. */
export function payloadActiveHelp(active: ActiveState): string {
  return PAYLOAD_ACTIVE_HELP[payloadActiveDisplay(active)] ?? "";
}

/**
 * The schema version a modifier needs: 9 when its payload active mirrors or
 * inverts the source's; else 8 exactly when it uses a v8 field — a
 * precondition, a post-release budget, or a step that `stepUsesV8` — else the
 * v7 rule's 7 or 6.
 */
export function requiredSchemaVersionV8(m: V8ModifierFields): number {
  if (isRelativePayloadActive(m.payloadActive)) return MODIFIER_SCHEMA_V9;
  const usesV8 =
    m.preconditions.length > 0 || m.postReleaseBudgetMs !== 0 || m.programSteps.some(stepUsesV8);
  return usesV8 ? MODIFIER_SCHEMA_V8 : requiredSchemaVersion(m);
}

/**
 * Step programs — every kind but WAIT, per-step channels, the floor source,
 * pass-if-current, always-run, labels and the post-release budget — belong to
 * an Outbound Before modifier, the only one that holds a command to release.
 */
export function isProgramVisible(type: ModifierType, direction: ModifierDirection): boolean {
  return type === ModifierType.BEFORE && direction === ModifierDirection.OUTBOUND;
}

/** Preconditions apply wherever a payload is sent: Before and After. */
export function arePreconditionsVisible(type: ModifierType): boolean {
  return type === ModifierType.BEFORE || type === ModifierType.AFTER;
}

/** The index of the RELEASE step, or -1 when the program has none. */
export function releaseIndex(steps: readonly { kind: SequenceStepKind }[]): number {
  return steps.findIndex((s) => s.kind === SequenceStepKind.RELEASE);
}

/** True for a step that runs after the RELEASE step, once the command has gone. */
export function isPostRelease(
  steps: readonly { kind: SequenceStepKind }[],
  index: number,
): boolean {
  const release = releaseIndex(steps);
  return release >= 0 && index > release;
}

/**
 * A step's floor source matters for a SEND (the floor it sends to) and for a
 * WAIT whose scope compares floors; Any floor compares none.
 */
export function isFloorSourceVisible(step: {
  kind: SequenceStepKind;
  scope: SequenceStepScope;
}): boolean {
  return (
    step.kind === SequenceStepKind.SEND ||
    (step.kind === SequenceStepKind.WAIT && step.scope !== SequenceStepScope.ANY_POSITION)
  );
}

const STEP_KIND_LABEL: Record<SequenceStepKind, string> = {
  [SequenceStepKind.WAIT]: "Wait",
  [SequenceStepKind.RELEASE]: "Release",
  [SequenceStepKind.SEND]: "Send",
  [SequenceStepKind.AWAIT_ACK]: "Await acknowledgement",
};

/** The name the wizard shows for a step kind. */
export function stepKindLabel(kind: SequenceStepKind): string {
  return STEP_KIND_LABEL[kind] ?? `Kind ${kind}`;
}

function trimmedLabel(label: string): string | null {
  const text = label.trim();
  return text === "" ? null : text;
}

/**
 * A form channel id as a channel reference. "" names no channel and sends none,
 * as does the all-zero UUID: LiftCloud stores a sent one as a real foreign key.
 */
function channelRef(id: string): UuidT | null {
  const uuid = stringToUuidT(id);
  return uuidToString(uuid) === ZERO_UUID_STRING ? null : uuid;
}

/**
 * One form step as it goes on the wire; `postRelease` gates always-run. Every
 * field starts at its schema default, the pre-v8 meaning, and a kind sets only
 * the fields it uses.
 */
function programWireStep(form: ProgramFormStep, postRelease: boolean): ModifierSequenceStepT {
  const step = new ModifierSequenceStepT();
  step.timeoutMs = form.timeoutMs;
  step.kind = form.kind;
  step.positionSource = isFloorSourceVisible(form)
    ? form.positionSource
    : StepPositionSource.PAYLOAD;
  step.alwaysRun = postRelease && form.alwaysRun;
  step.label = trimmedLabel(form.label);
  switch (form.kind) {
    case SequenceStepKind.WAIT:
      step.expectedMessageContent = form.messageRef?.messageContent ?? MessageContent.NONE;
      step.expectedMessageValue = form.messageRef?.messageValue ?? 0;
      step.expectedActive = form.expectedActive;
      step.scope = form.scope;
      step.observeChannelId = channelRef(form.observeChannelId);
      step.satisfiedIfCurrent = form.satisfiedIfCurrent;
      break;
    case SequenceStepKind.RELEASE:
      // VTS gives a RELEASE no timeout of its own.
      step.timeoutMs = 0;
      break;
    case SequenceStepKind.SEND:
      step.targetChannelId = channelRef(form.targetChannelId);
      step.sendMessageContent = form.sendRef?.messageContent ?? MessageContent.NONE;
      step.sendMessageValue = form.sendRef?.messageValue ?? 0;
      step.payloadActive = form.sendActive;
      break;
    default:
      break;
  }
  return step;
}

/**
 * The program as sent. A WAIT step left without a message is dropped, as an
 * empty step always was; every other kind needs none.
 */
function programWireSteps(steps: readonly ProgramFormStep[]): ModifierSequenceStepT[] {
  const kept = steps.filter((s) => s.kind !== SequenceStepKind.WAIT || s.messageRef !== null);
  const release = releaseIndex(kept);
  return kept.map((s, i) => programWireStep(s, release >= 0 && i > release));
}

/**
 * One precondition as it goes on the wire. The schema requires its channel, so
 * a row left without one fails at pack time, as a modifier without a source
 * channel does.
 */
function wirePrecondition(row: PreconditionRow): ModifierStateConditionT {
  const condition = new ModifierStateConditionT();
  condition.channelId = channelRef(row.channelId);
  condition.messageContent = row.messageRef?.messageContent ?? MessageContent.NONE;
  condition.messageValue = row.messageRef?.messageValue ?? 0;
  condition.expectedActive = row.expectedActive;
  condition.positionSource = row.positionSource;
  condition.scope = row.scope;
  condition.ifUnknown = row.ifUnknown;
  return condition;
}

/**
 * What the form actually saves for the v7 and v8 fields.
 *
 * A modifier that uses no v8 field is sent exactly as `effectiveV7Fields` sends
 * it — same steps, same keys — so a v7 modifier edited here saves unchanged.
 * Otherwise the program goes out step for step, and because every program step
 * can hold the command (or follow its release), the timeout policy is kept
 * whenever the program has a step, even one with no WAIT in it.
 */
export function effectiveV8Fields(form: V8Form): EffectiveV8Fields {
  const v7 = effectiveV7Fields(form);
  const preconditions = arePreconditionsVisible(form.modifierType)
    ? form.preconditions.map(wirePrecondition)
    : [];
  const programSteps = isProgramVisible(form.modifierType, form.direction)
    ? programWireSteps(form.expectedSequence)
    : [];
  const postReleaseBudgetMs = releaseIndex(programSteps) >= 0 ? form.postReleaseBudgetMs : 0;
  const schemaVersion = requiredSchemaVersionV8({
    ...v7,
    programSteps,
    preconditions,
    postReleaseBudgetMs,
  });
  if (schemaVersion < MODIFIER_SCHEMA_V8) {
    return { ...v7, preconditions: [], postReleaseBudgetMs: 0 };
  }
  if (programSteps.length === 0) {
    return { ...v7, preconditions, postReleaseBudgetMs, schemaVersion };
  }
  return {
    ...v7,
    steps: programSteps,
    timeoutPolicy: form.timeoutPolicy,
    dropAction:
      form.timeoutPolicy === ModifierTimeoutPolicy.DROP ? form.dropAction : ModifierDropAction.NONE,
    preconditions,
    postReleaseBudgetMs,
    schemaVersion,
  };
}

/** True when `channelId` is known to sit on another server than `sourceId`. */
function onAnotherServer(
  channelId: string,
  sourceId: string,
  channelServerMap: Readonly<Record<string, string>>,
): boolean {
  const server = channelServerMap[channelId];
  const sourceServer = channelServerMap[sourceId];
  return channelId !== "" && !!server && !!sourceServer && server !== sourceServer;
}

const FOREIGN_STEP_CHANNEL =
  "names a channel on another server; every channel a program uses must be on the source channel's server.";

/** What came before a step: the RELEASE step's number (0 for none) and any AWAIT_ACK. */
interface StepsBefore {
  release: number;
  awaitAck: boolean;
}

/** The first problem with one program step, numbered `n`, given the steps before it. */
function programStepProblem(
  step: ProgramFormStep,
  n: number,
  seen: StepsBefore,
  form: V8Form,
  channelServerMap: Readonly<Record<string, string>>,
): string | null {
  const label = stepKindLabel(step.kind);
  switch (step.kind) {
    case SequenceStepKind.WAIT:
      if (step.messageRef === null) return null;
      if (
        seen.release === 0 &&
        step.observeChannelId !== "" &&
        step.observeChannelId === form.sourceChannelId
      ) {
        return (
          `Step ${n} waits on the source channel before the Release step, but the source ` +
          "channel is held until the release — observe another channel or move the step after the Release."
        );
      }
      return onAnotherServer(step.observeChannelId, form.sourceChannelId, channelServerMap)
        ? `Step ${n} ${FOREIGN_STEP_CHANNEL}`
        : null;
    case SequenceStepKind.RELEASE:
      return seen.release > 0
        ? `Steps ${seen.release} and ${n} are both Release steps — a program releases the held command once.`
        : null;
    case SequenceStepKind.SEND:
      if (seen.release === 0) return `Step ${n} (${label}) must come after the Release step.`;
      if (step.sendRef?.messageContent !== MessageContent.Control_ControlMessage) {
        return `Step ${n} (${label}) needs a Control message to send.`;
      }
      return onAnotherServer(step.targetChannelId, form.sourceChannelId, channelServerMap)
        ? `Step ${n} ${FOREIGN_STEP_CHANNEL}`
        : null;
    default:
      if (seen.release === 0) return `Step ${n} (${label}) must come after the Release step.`;
      return seen.awaitAck
        ? `Step ${n} is a second ${label} step — a program awaits the controller's acknowledgement once.`
        : null;
  }
}

/**
 * The first reason the step program cannot be saved, or null.
 *
 * The rules VTS applies before it runs a program (and LiftCloud before it
 * stores one): one RELEASE at most; SEND and AWAIT_ACK only after it, AWAIT_ACK
 * once; a SEND sends a Control message; a WAIT before the release does not
 * observe the source channel, whose traffic is held until then; every channel
 * on the source's server. A modifier that cannot run a program — anything but
 * an Outbound Before — may only have WAIT steps. Steps are numbered from 1, as
 * the form shows them.
 */
export function programProblem(
  form: V8Form,
  channelServerMap: Readonly<Record<string, string>>,
): string | null {
  if (!isSequenceVisible(form.modifierType, form.direction)) return null;
  if (!isProgramVisible(form.modifierType, form.direction)) {
    const i = form.expectedSequence.findIndex((s) => s.kind !== SequenceStepKind.WAIT);
    if (i < 0) return null;
    return (
      `Step ${i + 1} is a ${stepKindLabel(form.expectedSequence[i]!.kind)} step, which only an ` +
      "Outbound Before modifier can run — change it to Wait or remove it."
    );
  }
  const kinds = form.expectedSequence.map((s) => s.kind);
  const problems = form.expectedSequence.map((step, i) => {
    const before = kinds.slice(0, i);
    const seen: StepsBefore = {
      release: before.indexOf(SequenceStepKind.RELEASE) + 1,
      awaitAck: before.includes(SequenceStepKind.AWAIT_ACK),
    };
    return programStepProblem(step, i + 1, seen, form, channelServerMap);
  });
  return problems.find((p) => p !== null) ?? null;
}

/**
 * The first reason a precondition cannot be saved, or null: each needs a
 * channel on the source's server, a message, and an expected level of Active
 * or Inactive. Numbered from 1, as the form shows them.
 */
export function preconditionProblem(
  form: V8Form,
  channelServerMap: Readonly<Record<string, string>>,
): string | null {
  if (!arePreconditionsVisible(form.modifierType)) return null;
  const problems = form.preconditions.map((row, i) => {
    const n = i + 1;
    if (row.channelId === "") return `Precondition ${n} needs a channel.`;
    if (row.messageRef === null) return `Precondition ${n} needs a message.`;
    if (row.expectedActive !== ActiveState.ACTIVE && row.expectedActive !== ActiveState.INACTIVE) {
      return `Precondition ${n} must expect Active or Inactive.`;
    }
    return onAnotherServer(row.channelId, form.sourceChannelId, channelServerMap)
      ? `Precondition ${n} names a channel on another server; it must be on the source channel's server.`
      : null;
  });
  return problems.find((p) => p !== null) ?? null;
}

/** Every channel on the source channel's server, minus `exclude`. */
function sameServerChannels(
  channels: readonly ObservableChannel[],
  sourceChannelId: string,
  exclude: string,
): { label: string; value: string }[] {
  const source = channels.find((c) => c.id === sourceChannelId);
  if (!source) return [];
  return channels
    .filter((c) => c.serverId === source.serverId && c.id !== exclude)
    .map((c) => ({ label: c.name, value: c.id }));
}

/**
 * A WAIT step's channel choices: the modifier's observe channel (the default,
 * stored as ""), then every channel on the source's server — minus the source
 * itself before the release, while its traffic is held.
 */
export function stepObserveOptions(
  channels: readonly ObservableChannel[],
  sourceChannelId: string,
  preRelease: boolean,
): { label: string; value: string }[] {
  return [
    { label: "Modifier's observe channel", value: "" },
    ...sameServerChannels(channels, sourceChannelId, preRelease ? sourceChannelId : ""),
  ];
}

/**
 * A SEND step's channel choices: the modifier's target (the default, stored as
 * ""), then every channel on the source's server.
 */
export function stepTargetOptions(
  channels: readonly ObservableChannel[],
  sourceChannelId: string,
): { label: string; value: string }[] {
  return [
    { label: "Target channel", value: "" },
    ...sameServerChannels(channels, sourceChannelId, ""),
  ];
}

/** A precondition's channel choices: every channel on the source's server. */
export function preconditionChannelOptions(
  channels: readonly ObservableChannel[],
  sourceChannelId: string,
): { label: string; value: string }[] {
  return sameServerChannels(channels, sourceChannelId, "");
}

/**
 * A draft step's v8 fields, whatever version of the form wrote the draft. A
 * draft saved before v8 has none of them and reads as a plain WAIT; a value
 * that is not of its field's type reads as the default.
 */
export function programStepFieldsFromDraft<R extends LeafRef>(
  raw: Partial<ProgramStepFields<R>>,
): ProgramStepFields<R> {
  const d = DEFAULT_PROGRAM_STEP_FIELDS;
  return {
    kind: enumMember(SequenceStepKind, raw.kind, d.kind),
    observeChannelId: typeof raw.observeChannelId === "string" ? raw.observeChannelId : "",
    positionSource: enumMember(StepPositionSource, raw.positionSource, d.positionSource),
    satisfiedIfCurrent: raw.satisfiedIfCurrent === true,
    alwaysRun: raw.alwaysRun === true,
    targetChannelId: typeof raw.targetChannelId === "string" ? raw.targetChannelId : "",
    sendRef: raw.sendRef ?? null,
    sendActive: enumMember(ActiveState, raw.sendActive, d.sendActive),
    label: typeof raw.label === "string" ? raw.label : "",
  };
}

/** A draft precondition row, read the same way as a draft step. */
export function preconditionRowFromDraft<R extends LeafRef>(
  raw: Partial<PreconditionRow<R>>,
): PreconditionRow<R> {
  const d = NEW_PRECONDITION;
  return {
    channelId: typeof raw.channelId === "string" ? raw.channelId : "",
    messageRef: raw.messageRef ?? null,
    expectedActive: enumMember(ActiveState, raw.expectedActive, d.expectedActive),
    positionSource: enumMember(StepPositionSource, raw.positionSource, d.positionSource),
    scope: enumMember(SequenceStepScope, raw.scope, d.scope),
    ifUnknown: enumMember(PreconditionUnknown, raw.ifUnknown, d.ifUnknown),
  };
}

/** Channel id or "": an unset reference, or the all-zero UUID, reads as "". */
export function channelIdOrEmpty(uuid: UuidT | null): string {
  const id = uuidToString(uuid);
  return id === ZERO_UUID_STRING ? "" : id;
}

/**
 * A stored step's v8 fields as the form holds them. Pass null for a step of a
 * row below v8, where they mean nothing: it reads as a plain WAIT. A value
 * outside its enum reads as the pre-v8 meaning. `refFor` turns the stored SEND
 * leaf into whatever reference the form's message picker holds.
 */
export function programStepFieldsFromStored<R extends LeafRef>(
  step: ModifierSequenceStepT | null,
  refFor: (messageContent: number, messageValue: number) => R,
): ProgramStepFields<R> {
  if (step === null) return { ...DEFAULT_PROGRAM_STEP_FIELDS };
  return {
    kind: enumMember(SequenceStepKind, step.kind, SequenceStepKind.WAIT),
    observeChannelId: channelIdOrEmpty(step.observeChannelId),
    positionSource: enumMember(StepPositionSource, step.positionSource, StepPositionSource.PAYLOAD),
    satisfiedIfCurrent: step.satisfiedIfCurrent,
    alwaysRun: step.alwaysRun,
    targetChannelId: channelIdOrEmpty(step.targetChannelId),
    sendRef:
      step.sendMessageContent !== MessageContent.NONE
        ? refFor(step.sendMessageContent, step.sendMessageValue)
        : null,
    sendActive: enumMember(ActiveState, step.payloadActive, ActiveState.UNSET),
    label: typeof step.label === "string" ? step.label : "",
  };
}

/** A stored precondition as the form holds it; `refFor` as for a step. */
export function preconditionRowFromStored<R extends LeafRef>(
  c: ModifierStateConditionT,
  refFor: (messageContent: number, messageValue: number) => R,
): PreconditionRow<R> {
  return {
    channelId: channelIdOrEmpty(c.channelId),
    messageRef:
      c.messageContent !== MessageContent.NONE ? refFor(c.messageContent, c.messageValue) : null,
    expectedActive: enumMember(ActiveState, c.expectedActive, ActiveState.UNSET),
    positionSource: enumMember(StepPositionSource, c.positionSource, StepPositionSource.PAYLOAD),
    scope: enumMember(SequenceStepScope, c.scope, SequenceStepScope.SAME_POSITION),
    ifUnknown: enumMember(PreconditionUnknown, c.ifUnknown, PreconditionUnknown.ASSUME_MET),
  };
}

/**
 * What to add to a failed save's message when LiftCloud may have refused it for
 * being v8: LiftCloud accepts v8 modifiers only while CHANNEL_MODIFIER_V8_ENABLED
 * is on. Null unless the modifier is v8 and the reply was an HTTP 4xx other than
 * 401 (an expired session, which says nothing about v8).
 */
export function v8RefusalHint(schemaVersion: number, error: unknown): string | null {
  if (schemaVersion !== MODIFIER_SCHEMA_V8 || error === null || typeof error !== "object") {
    return null;
  }
  const { status } = error as { status?: unknown };
  if (typeof status !== "number" || status < 400 || status > 499 || status === 401) return null;
  return (
    "If LiftCloud has schema v8 authoring switched off (CHANNEL_MODIFIER_V8_ENABLED), it " +
    "refuses this modifier, which uses a step program, preconditions or a post-release " +
    "budget. The form keeps everything entered, so it can be saved once v8 is on."
  );
}
